import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, BookmarkPlusIcon, BookmarkIcon, CalendarPlusIcon, UserPlusIcon, ClockIcon, MapPinIcon, GlobeIcon, BookOpenIcon, TagIcon, HeadphonesIcon, CalendarIcon, TrophyIcon, BriefcaseIcon, UsersIcon, StarIcon, ShareIcon, DollarSignIcon, MicIcon, MoreHorizontalIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export type UnifiedPostType = 'New Release' | 'Cover Reveal' | 'Sale' | 'Audiobook Launch' | 'Vendor Availability' | 'Collaboration Request' | 'Event Announcement' | 'Award Nomination' | 'Service Launch';
export interface UnifiedUpdatePost {
  id: number | string;
  type: UnifiedPostType;
  title: string;
  description: string;
  author: string;
  date: string;
  image?: string;
  genre?: string;
  serviceCategory?: string;
  profileLink?: string;
  bookLink?: string;
  seriesName?: string;
  bookNumber?: string | number;
  saleEndsAt?: string;
  formats?: string[];
  location?: string;
  availability?: string;
  vendorName?: string;
  narrator?: string;
  rating?: number;
  reviewCount?: number;
  originalPrice?: number;
  salePrice?: number;
  eventDate?: string;
  releaseDate?: string;
  slots?: number;
  deadline?: string;
  turnaround?: string;
  priceRange?: string;
  isNew?: boolean;
}
const typeBadgeColors: Record<UnifiedPostType, string> = {
  'New Release': 'bg-emerald-100 text-emerald-800',
  'Cover Reveal': 'bg-pink-100 text-pink-800',
  Sale: 'bg-orange-100 text-orange-800',
  'Audiobook Launch': 'bg-blue-100 text-blue-800',
  'Vendor Availability': 'bg-purple-100 text-purple-800',
  'Collaboration Request': 'bg-teal-100 text-teal-800',
  'Event Announcement': 'bg-amber-100 text-amber-800',
  'Award Nomination': 'bg-yellow-100 text-yellow-800',
  'Service Launch': 'bg-violet-100 text-violet-800'
};
const typeBadgeColorsDark: Record<UnifiedPostType, string> = {
  'New Release': 'bg-emerald-900/40 text-emerald-300',
  'Cover Reveal': 'bg-pink-900/40 text-pink-300',
  Sale: 'bg-orange-900/40 text-orange-300',
  'Audiobook Launch': 'bg-blue-900/40 text-blue-300',
  'Vendor Availability': 'bg-purple-900/40 text-purple-300',
  'Collaboration Request': 'bg-teal-900/40 text-teal-300',
  'Event Announcement': 'bg-amber-900/40 text-amber-300',
  'Award Nomination': 'bg-yellow-900/40 text-yellow-300',
  'Service Launch': 'bg-violet-900/40 text-violet-300'
};
const typeAccentBorder: Record<UnifiedPostType, string> = {
  'New Release': 'border-l-emerald-400',
  'Cover Reveal': 'border-l-pink-400',
  Sale: 'border-l-orange-400',
  'Audiobook Launch': 'border-l-blue-400',
  'Vendor Availability': 'border-l-purple-400',
  'Collaboration Request': 'border-l-teal-400',
  'Event Announcement': 'border-l-amber-400',
  'Award Nomination': 'border-l-yellow-400',
  'Service Launch': 'border-l-violet-400'
};
const typePlaceholderBg: Record<UnifiedPostType, string> = {
  'New Release': 'bg-emerald-50 text-emerald-300',
  'Cover Reveal': 'bg-pink-50 text-pink-300',
  Sale: 'bg-orange-50 text-orange-300',
  'Audiobook Launch': 'bg-blue-50 text-blue-300',
  'Vendor Availability': 'bg-purple-50 text-purple-300',
  'Collaboration Request': 'bg-teal-50 text-teal-300',
  'Event Announcement': 'bg-amber-50 text-amber-300',
  'Award Nomination': 'bg-yellow-50 text-yellow-300',
  'Service Launch': 'bg-violet-50 text-violet-300'
};
const typePlaceholderBgDark: Record<UnifiedPostType, string> = {
  'New Release': 'bg-emerald-900/20 text-emerald-700',
  'Cover Reveal': 'bg-pink-900/20 text-pink-700',
  Sale: 'bg-orange-900/20 text-orange-700',
  'Audiobook Launch': 'bg-blue-900/20 text-blue-700',
  'Vendor Availability': 'bg-purple-900/20 text-purple-700',
  'Collaboration Request': 'bg-teal-900/20 text-teal-700',
  'Event Announcement': 'bg-amber-900/20 text-amber-700',
  'Award Nomination': 'bg-yellow-900/20 text-yellow-700',
  'Service Launch': 'bg-violet-900/20 text-violet-700'
};
const readerCTAs: Record<UnifiedPostType, string> = {
  'New Release': 'View Details',
  'Cover Reveal': 'View Details',
  Sale: 'Shop Now',
  'Audiobook Launch': 'Listen Now',
  'Vendor Availability': 'View Profile',
  'Collaboration Request': 'Learn More',
  'Event Announcement': 'View Event',
  'Award Nomination': 'Vote',
  'Service Launch': 'View Service'
};
const vendorCTAs: Record<UnifiedPostType, string> = {
  'New Release': 'View Details',
  'Cover Reveal': 'View Details',
  Sale: 'View Details',
  'Audiobook Launch': 'View Details',
  'Vendor Availability': 'View Profile',
  'Collaboration Request': 'Respond',
  'Event Announcement': 'Save Event',
  'Award Nomination': 'Submit Work',
  'Service Launch': 'View Service'
};
const PlaceholderIcon: React.FC<{
  type: UnifiedPostType;
  className?: string;
}> = ({
  type,
  className = 'h-7 w-7'
}) => {
  const props = {
    className
  };
  switch (type) {
    case 'New Release':
    case 'Cover Reveal':
      return <BookOpenIcon {...props} data-id="element-3547" data-mp="9dygd3so6ubp" />;
    case 'Audiobook Launch':
      return <HeadphonesIcon {...props} data-id="element-3548" data-mp="3rkuuubkmw4nt" />;
    case 'Sale':
      return <TagIcon {...props} data-id="element-3549" data-mp="3rmfo7xv7phk3" />;
    case 'Event Announcement':
      return <CalendarIcon {...props} data-id="element-3550" data-mp="3vk3nfd3xtwhs" />;
    case 'Award Nomination':
      return <TrophyIcon {...props} data-id="element-3551" data-mp="3hu07f3vniz0s" />;
    case 'Vendor Availability':
    case 'Service Launch':
      return <BriefcaseIcon {...props} data-id="element-3552" data-mp="3ml9wo3dag0c5" />;
    case 'Collaboration Request':
      return <UsersIcon {...props} data-id="element-3553" data-mp="37w424speayal" />;
    default:
      return <BookOpenIcon {...props} data-id="element-3554" data-mp="37xf5wsiuc61p" />;
  }
};
interface UnifiedUpdateCardProps {
  post: UnifiedUpdatePost;
  className?: string;
}
export const UnifiedUpdateCard: React.FC<UnifiedUpdateCardProps> = ({
  post,
  className = ''
}) => {
  const dk = useDarkClasses();
  const {
    isReader
  } = useUserRole();
  const rt = useRoleTheme();
  const [wishlisted, setWishlisted] = useState(false);
  const [tracked, setTracked] = useState(false);
  const [seriesTracked, setSeriesTracked] = useState(false);
  const [savedForLater, setSavedForLater] = useState(false);
  const [following, setFollowing] = useState(false);
  const [savedContact, setSavedContact] = useState(false);
  const [categoryTracked, setCategoryTracked] = useState(false);
  const [savedInspiration, setSavedInspiration] = useState(false);
  const [calendarAdded, setCalendarAdded] = useState(false);
  const [shared, setShared] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);
  const isBookType = post.type === 'New Release' || post.type === 'Cover Reveal';
  const isCoverService = (post.type === 'Service Launch' || post.type === 'Vendor Availability' || post.type === 'Sale') && (post.serviceCategory?.toLowerCase().includes('cover') || post.title.toLowerCase().includes('cover') || post.title.toLowerCase().includes('pre-made'));
  const isBookShape = isBookType || isCoverService;
  const isAudioType = post.type === 'Audiobook Launch';
  const hasRating = (isBookType || isAudioType) && post.rating;
  const daysRemaining = post.saleEndsAt ? Math.ceil((new Date(post.saleEndsAt).getTime() - Date.now()) / 86400000) : null;
  const isNew = post.isNew ?? (() => {
    try {
      const d = new Date(post.date);
      return Date.now() - d.getTime() < 7 * 86400000;
    } catch {
      return false;
    }
  })();
  const ctaText = isReader ? readerCTAs[post.type] : vendorCTAs[post.type];
  const ctaLink = post.bookLink || post.profileLink || '#';
  const cardBg = dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = dk.isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = dk.isDark ? 'text-gray-400' : 'text-gray-500';
  const textTertiary = dk.isDark ? 'text-gray-500' : 'text-gray-400';
  const badgeColor = dk.isDark ? typeBadgeColorsDark[post.type] : typeBadgeColors[post.type];
  const primaryButtonClass = isReader ? dk.isDark ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' : dk.isDark ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60' : 'bg-purple-100 text-purple-700 hover:bg-purple-200';
  const menuItemClass = `w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${dk.isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`;
  const menuItemActiveClass = `w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-md ${isReader ? dk.isDark ? 'text-indigo-300 bg-indigo-900/30' : 'text-indigo-700 bg-indigo-50' : dk.isDark ? 'text-purple-300 bg-purple-900/30' : 'text-purple-700 bg-purple-50'}`;
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        url: ctaLink
      });
    } else {
      navigator.clipboard?.writeText(window.location.origin + ctaLink);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
    setMenuOpen(false);
  };
  // Build save menu items based on type + role
  const getSaveMenuItems = () => {
    const items: React.ReactNode[] = [];
    if (isReader) {
      switch (post.type) {
        case 'New Release':
        case 'Audiobook Launch':
          items.push(<button key="wishlist" onClick={() => {
            setWishlisted(v => !v);
            setMenuOpen(false);
          }} className={wishlisted ? menuItemActiveClass : menuItemClass} data-id="element-3555" data-mp="34os2g7p0v00h">
              <HeartIcon className={`h-3.5 w-3.5 ${wishlisted ? 'fill-current' : ''}`} data-id="element-3556" data-mp="1xpbwtqr8jeje" />
              {wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
            </button>, <button key="tracker" onClick={() => {
            setTracked(v => !v);
            setMenuOpen(false);
          }} className={tracked ? menuItemActiveClass : menuItemClass} data-id="element-3557" data-mp="1oo47vzalzq1z">
              <BookmarkIcon className={`h-3.5 w-3.5 ${tracked ? 'fill-current' : ''}`} data-id="element-3558" data-mp="1err7kikk6dy9" />
              {tracked ? 'Remove from Tracker' : 'Add to My Tracker'}
            </button>, <button key="series" onClick={() => {
            setSeriesTracked(v => !v);
            setMenuOpen(false);
          }} className={seriesTracked ? menuItemActiveClass : menuItemClass} data-id="element-3559" data-mp="14yaa94l7qgpu">
              <BookmarkPlusIcon className="h-3.5 w-3.5" data-id="element-3560" data-mp="cey6eewoib3i" />
              {seriesTracked ? 'Untrack Series' : 'Track Series'}
            </button>);
          break;
        case 'Cover Reveal':
          items.push(<button key="wishlist" onClick={() => {
            setWishlisted(v => !v);
            setMenuOpen(false);
          }} className={wishlisted ? menuItemActiveClass : menuItemClass} data-id="element-3561" data-mp="ma9iepgpd47k">
              <HeartIcon className={`h-3.5 w-3.5 ${wishlisted ? 'fill-current' : ''}`} data-id="element-3562" data-mp="qcwiterfvxgd" />
              {wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
            </button>, <button key="series" onClick={() => {
            setSeriesTracked(v => !v);
            setMenuOpen(false);
          }} className={seriesTracked ? menuItemActiveClass : menuItemClass} data-id="element-3563" data-mp="10498bzprmbj8">
              <BookmarkPlusIcon className="h-3.5 w-3.5" data-id="element-3564" data-mp="1h5n2mswcwjmq" />
              {seriesTracked ? 'Untrack Series' : 'Track Series'}
            </button>);
          break;
        case 'Sale':
          items.push(<button key="later" onClick={() => {
            setSavedForLater(v => !v);
            setMenuOpen(false);
          }} className={savedForLater ? menuItemActiveClass : menuItemClass} data-id="element-3565" data-mp="1r29o9jwibept">
              <ClockIcon className="h-3.5 w-3.5" data-id="element-3566" data-mp="24ufsc7mdxv8w" />
              {savedForLater ? 'Remove from Saved' : 'Save for Later'}
            </button>, <button key="wishlist" onClick={() => {
            setWishlisted(v => !v);
            setMenuOpen(false);
          }} className={wishlisted ? menuItemActiveClass : menuItemClass} data-id="element-3567" data-mp="29v3tvn88puhd">
              <HeartIcon className={`h-3.5 w-3.5 ${wishlisted ? 'fill-current' : ''}`} data-id="element-3568" data-mp="keciicgir92h" />
              {wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
            </button>);
          break;
        case 'Event Announcement':
          items.push(<button key="cal" onClick={() => {
            setCalendarAdded(v => !v);
            setMenuOpen(false);
          }} className={calendarAdded ? menuItemActiveClass : menuItemClass} data-id="element-3569" data-mp="2owkcoin9vy82">
              <CalendarPlusIcon className="h-3.5 w-3.5" data-id="element-3570" data-mp="3glvwxz6kwu04" />
              {calendarAdded ? 'Remove from Calendar' : 'Add to Calendar'}
            </button>);
          break;
        case 'Vendor Availability':
        case 'Service Launch':
          items.push(<button key="follow" onClick={() => {
            setFollowing(v => !v);
            setMenuOpen(false);
          }} className={following ? menuItemActiveClass : menuItemClass} data-id="element-3571" data-mp="32nfh9iqqnt5u">
              <UserPlusIcon className="h-3.5 w-3.5" data-id="element-3572" data-mp="3f5zyinkx66u0" />
              {following ? 'Unfollow Vendor' : 'Follow Vendor'}
            </button>);
          break;
      }
    } else {
      switch (post.type) {
        case 'New Release':
        case 'Cover Reveal':
        case 'Audiobook Launch':
          items.push(<button key="inspo" onClick={() => {
            setSavedInspiration(v => !v);
            setMenuOpen(false);
          }} className={savedInspiration ? menuItemActiveClass : menuItemClass} data-id="element-3573" data-mp="3b57qtyrp49zu">
              <BookmarkPlusIcon className="h-3.5 w-3.5" data-id="element-3574" data-mp="3ovt9bmd8mhx7" />
              {savedInspiration ? 'Remove from Inspiration' : 'Save to Inspiration'}
            </button>);
          break;
        case 'Event Announcement':
          items.push(<button key="cal" onClick={() => {
            setCalendarAdded(v => !v);
            setMenuOpen(false);
          }} className={calendarAdded ? menuItemActiveClass : menuItemClass} data-id="element-3575" data-mp="2diqryp3bdobz">
              <CalendarPlusIcon className="h-3.5 w-3.5" data-id="element-3576" data-mp="25wd8xiykq1ov" />
              {calendarAdded ? 'Remove from Calendar' : 'Add to Calendar'}
            </button>);
          break;
        case 'Collaboration Request':
          items.push(<button key="contact" onClick={() => {
            setSavedContact(v => !v);
            setMenuOpen(false);
          }} className={savedContact ? menuItemActiveClass : menuItemClass} data-id="element-3577" data-mp="1r7yz2o2qpcyt">
              <UserPlusIcon className="h-3.5 w-3.5" data-id="element-3578" data-mp="20z3axex4chpf" />
              {savedContact ? 'Contact Saved' : 'Save Contact'}
            </button>);
          break;
        case 'Vendor Availability':
        case 'Service Launch':
          items.push(<button key="contact" onClick={() => {
            setSavedContact(v => !v);
            setMenuOpen(false);
          }} className={savedContact ? menuItemActiveClass : menuItemClass} data-id="element-3579" data-mp="13j69d9cpo1cn">
              <UserPlusIcon className="h-3.5 w-3.5" data-id="element-3580" data-mp="38he3g8dxyzq7" />
              {savedContact ? 'Contact Saved' : 'Save Contact'}
            </button>, <button key="track" onClick={() => {
            setCategoryTracked(v => !v);
            setMenuOpen(false);
          }} className={categoryTracked ? menuItemActiveClass : menuItemClass} data-id="element-3581" data-mp="3i9tlf5p8nob3">
              <TagIcon className="h-3.5 w-3.5" data-id="element-3582" data-mp="2ujzbox07ufv2" />
              {categoryTracked ? 'Untrack Category' : 'Track Service Category'}
            </button>);
          break;
      }
    }
    // Share always last
    items.push(<button key="share" onClick={handleShare} className={menuItemClass} data-id="element-3583" data-mp="9ui7s6c2rpax">
        <ShareIcon className="h-3.5 w-3.5" data-id="element-3584" data-mp="dv24j8sqkqwj" />
        {shared ? 'Link Copied!' : 'Share'}
      </button>);
    return items;
  };
  const placeholderBg = dk.isDark ? typePlaceholderBgDark[post.type] : typePlaceholderBg[post.type];
  const anyActionActive = wishlisted || tracked || seriesTracked || savedForLater || following || savedContact || categoryTracked || savedInspiration || calendarAdded;
  return <div className={`relative ${cardBg} border border-l-4 ${typeAccentBorder[post.type]} rounded-lg p-4 flex gap-4 items-start transition-shadow hover:shadow-md ${className}`} data-id="element-3585" data-mp="1klga02db89ld">
      {/* ⋯ Menu — top right */}
      <div ref={menuRef} className="absolute top-3 right-3 z-10" data-id="element-3586" data-mp="1apwe3fyqxawm">
        <button onClick={() => setMenuOpen(v => !v)} className={`p-1.5 rounded-md transition-colors ${menuOpen || anyActionActive ? isReader ? dk.isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-600' : dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-600' : dk.isDark ? 'text-gray-500 hover:bg-gray-700 hover:text-gray-300' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`} title="Save options" data-id="element-3587" data-mp="16mztxktk56qk">
          <MoreHorizontalIcon className="h-4 w-4" data-id="element-3588" data-mp="29tkeb6r4h3mz" />
        </button>
        {menuOpen && <div className={`absolute right-0 top-8 w-48 rounded-lg shadow-lg border p-1 z-20 ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3589" data-mp="1v569iqa5b53s">
            {getSaveMenuItems()}
          </div>}
      </div>
      {/* Image Zone */}
      <div className={`flex-shrink-0 ${isBookShape ? 'w-16 sm:w-20' : 'w-20 sm:w-24'}`} data-id="element-3590" data-mp="1q7xqknh6ax1h">
        {post.image ? <img src={post.image} alt={post.title} className={`w-full object-cover rounded-md shadow-sm ${isBookShape ? 'aspect-[2/3]' : 'aspect-square'}`} data-id="element-3591" data-mp="1c8y2pi1him6d" /> : <div className={`w-full rounded-md flex items-center justify-center ${isBookShape ? 'aspect-[2/3]' : 'aspect-square'} ${placeholderBg}`} data-id="element-3592" data-mp="36j1syb83200t">
            <PlaceholderIcon type={post.type} className="h-7 w-7 opacity-60" data-id="element-3593" data-mp="zxdruhilatmn" />
          </div>}
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0 pr-6" data-id="element-3594" data-mp="uvy9ix7sr17y">
        {/* Zone 1: Badges */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-id="element-3595" data-mp="19o02ryfd2vz0">
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${rt.badgeRadius} ${badgeColor}`} data-id="element-3596" data-mp="14uxh0cgtsqtd">
            {post.type}
          </span>
          {isNew && <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${rt.badgeRadius} bg-emerald-500 text-white`} data-id="element-3597" data-mp="1dnh9swafs16a">
              New
            </span>}
          {post.genre && <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold ${rt.badgeRadius} ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-3598" data-mp="1nklyp3vo2aqs">
              {post.genre}
            </span>}
          {post.serviceCategory && !isReader && <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold ${rt.badgeRadius} ${dk.isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-600'}`} data-id="element-3599" data-mp="1hexrisqbvly5">
              {post.serviceCategory}
            </span>}
          {/* Sale expiry */}
          {daysRemaining !== null && daysRemaining > 0 && <span className={`flex items-center gap-1 text-[10px] font-semibold ${daysRemaining <= 2 ? 'text-red-500' : 'text-amber-500'}`} data-id="element-3600" data-mp="yfthphqypo5f">
              <ClockIcon className="h-3 w-3" data-id="element-3601" data-mp="1dedsfjm7289d" />
              Ends in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
            </span>}
          {daysRemaining !== null && daysRemaining <= 0 && <span className={`flex items-center gap-1 text-[10px] font-semibold ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-3602" data-mp="2ouc4zxgkzcfc">
              <ClockIcon className="h-3 w-3" data-id="element-3603" data-mp="2rbm9gcdp865c" />
              Sale ended
            </span>}
        </div>
        {/* Zone 2: Title + series subtitle */}
        <div className="mb-1.5" data-id="element-3604" data-mp="2ibzcc2s05mi3">
          <h3 className={`text-base sm:text-lg font-bold leading-tight ${textPrimary}`} data-id="element-3605" data-mp="2id0vkuj69faw">
            {post.title}
          </h3>
          {post.seriesName && <p className={`text-xs font-medium mt-0.5 ${isReader ? 'text-indigo-500' : 'text-purple-500'}`} data-id="element-3606" data-mp="3a2vloteut90m">
              {post.bookNumber ? `Book ${post.bookNumber} of ` : ''}
              {post.seriesName}
            </p>}
        </div>
        {/* Zone 3: Author (prominent) + narrator */}
        <div className="mb-1.5" data-id="element-3607" data-mp="35avqtfdn56aw">
          <span className={`text-sm font-semibold ${textPrimary}`} data-id="element-3608" data-mp="35a2kgfilgita">
            {post.author || post.vendorName}
          </span>
          {post.narrator && <span className={`text-xs ml-1.5 ${textSecondary}`} data-id="element-3609" data-mp="1owr9w7j5vp8">
              · <MicIcon className="h-3 w-3 inline opacity-60 mr-0.5" data-id="element-3610" data-mp="bjlv4hsqw24u" />
              {post.narrator}
            </span>}
        </div>
        {/* Zone 3.5: Single-star rating */}
        {hasRating && <div className={`flex items-center gap-1.5 mb-1.5 text-xs ${textSecondary}`} data-id="element-3611" data-mp="2hl0lsowbu6v">
            <StarIcon className="h-3.5 w-3.5 text-amber-400 fill-amber-400" data-id="element-3612" data-mp="3to5leuocy5vg" />
            <span className="font-semibold text-amber-500" data-id="element-3613" data-mp="3oq40e47dfwhc">
              {post.rating!.toFixed(1)}
            </span>
            {post.reviewCount && <span className={textTertiary} data-id="element-3614" data-mp="3jxaydo3af28y">
                · {post.reviewCount.toLocaleString()} reviews
              </span>}
          </div>}
        {/* Zone 4: Release date (book/audio) or event date */}
        {(post.releaseDate || post.type === 'Event Announcement' && post.eventDate) && <div className={`flex items-center gap-1 text-xs font-medium mb-1.5 ${post.type === 'Event Announcement' ? dk.isDark ? 'text-amber-400' : 'text-amber-600' : textSecondary}`} data-id="element-3615" data-mp="30zroki909flh">
            <CalendarIcon className="h-3.5 w-3.5 opacity-70" data-id="element-3616" data-mp="2vumoovo2izy7" />
            {post.type === 'Event Announcement' ? post.eventDate : `Released ${post.releaseDate}`}
          </div>}
        {/* Zone 4.5: Price for Sale */}
        {post.type === 'Sale' && (post.originalPrice || post.salePrice) && <div className="flex items-center gap-2 mb-1.5" data-id="element-3617" data-mp="287ocia6zee63">
            {post.salePrice && <span className="text-sm font-bold text-orange-500" data-id="element-3618" data-mp="2d57r5b7lcsyk">
                ${post.salePrice.toFixed(2)}
              </span>}
            {post.originalPrice && <span className={`text-xs line-through ${textTertiary}`} data-id="element-3619" data-mp="2mvj6rv0n0qla">
                ${post.originalPrice.toFixed(2)}
              </span>}
            {post.originalPrice && post.salePrice && <span className={`text-xs font-semibold text-orange-500 px-1.5 py-0.5 rounded ${dk.isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`} data-id="element-3620" data-mp="1ucvnml57blmu">
                {Math.round((1 - post.salePrice / post.originalPrice) * 100)}%
                off
              </span>}
          </div>}
        {/* Zone 4.7: Slots/deadline/turnaround/price for service types */}
        {(post.type === 'Collaboration Request' || post.type === 'Vendor Availability' || post.type === 'Service Launch') && (post.slots !== undefined || post.deadline || post.turnaround || post.priceRange) && <div className={`flex flex-wrap gap-3 mb-1.5 text-xs ${textTertiary}`} data-id="element-3621" data-mp="smdqg30cygjd">
              {post.slots !== undefined && <span className={`flex items-center gap-1 font-medium ${post.slots <= 2 ? 'text-red-500' : dk.isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-3622" data-mp="2udqs8j7148b7">
                  <UsersIcon className="h-3 w-3" data-id="element-3623" data-mp="14wlih2hbrpeu" />
                  {post.slots} slot{post.slots !== 1 ? 's' : ''} available
                </span>}
              {post.deadline && <span className="flex items-center gap-1" data-id="element-3624" data-mp="v7v7x287yt48">
                  <ClockIcon className="h-3 w-3 opacity-60" data-id="element-3625" data-mp="v5933acpqrpp" />
                  Deadline: {post.deadline}
                </span>}
              {post.turnaround && <span className="flex items-center gap-1" data-id="element-3626" data-mp="m52ht2arwnfz">
                  <ClockIcon className="h-3 w-3 opacity-60" data-id="element-3627" data-mp="r69qd3kqpqnp" />
                  {post.turnaround} turnaround
                </span>}
              {post.priceRange && <span className="flex items-center gap-1" data-id="element-3628" data-mp="h7api767uzb9">
                  <DollarSignIcon className="h-3 w-3 opacity-60" data-id="element-3629" data-mp="h7sv3uerqpvu" />
                  {post.priceRange}
                </span>}
            </div>}
        {/* Zone 5: Description */}
        <p className={`text-sm line-clamp-2 mb-2.5 ${textSecondary}`} data-id="element-3630" data-mp="2bksezzq6m68x">
          {post.description}
        </p>
        {/* Zone 6: Formats / location / availability */}
        {(post.formats || post.location || post.availability) && <div className={`flex flex-wrap gap-3 mb-2.5 text-xs ${textTertiary}`} data-id="element-3631" data-mp="205ytixjglds9">
            {post.formats && <div className="flex items-center gap-1" data-id="element-3632" data-mp="1w5yfhe09v4lq">
                <BookOpenIcon className="h-3.5 w-3.5 opacity-60" data-id="element-3633" data-mp="2summlfag97gd" />
                {post.formats.join(', ')}
              </div>}
            {post.location && <div className="flex items-center gap-1" data-id="element-3634" data-mp="2nxw3sk79fxr9">
                <MapPinIcon className="h-3.5 w-3.5 opacity-60" data-id="element-3635" data-mp="2j41ff3gpmulu" />
                {post.location}
              </div>}
            {post.availability && <div className="flex items-center gap-1" data-id="element-3636" data-mp="3bl5escra76dr">
                <GlobeIcon className="h-3.5 w-3.5 opacity-60" data-id="element-3637" data-mp="3bmothyxah9df" />
                {post.availability}
              </div>}
          </div>}
        {/* Zone 7: CTA + posted date */}
        <div className="flex items-center justify-between pt-1" data-id="element-3638" data-mp="tdvomrb8jgl1">
          <Link to={ctaLink} className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${primaryButtonClass}`} data-id="element-3639" data-mp="yacepr1zw50r">
            {ctaText}
          </Link>
          <span className={`text-[10px] ${textTertiary}`} data-id="element-3640" data-mp="17ddgxgnqpbz5">
            Posted {post.date}
          </span>
        </div>
      </div>
    </div>;
};